import { useLang } from '@/i18n';
import { getSolarRecommand } from '@/service/solar-calculator';
import gtm from '@/utils/gtm';
import { FormatMoneyLocal } from '@elem/ui-common/utils/FormatMoneyLocal';
import EcoImg from 'eco-design/base/eco-img';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import css from './index.module.scss';
import { langText } from './langText';

const SectionCard: React.FC<{ callbackCart: (item: any) => void }> = ({ callbackCart }) => {
    const { t } = useLang('d3u.sectionCard');
    const { locale } = useRouter();
    const i18n = langText[locale] || langText['jp'];
    const [list, setList] = useState([]);
    const PRODUCT_CONFIG = {
        jp: {
            item1: { productId: '14803254935919', variantId: '52291938976111' },
            item2: { productId: '14803254870383', variantId: '52291943104879' },
        },
    };

    const getProductConfig = (itemName: any) => PRODUCT_CONFIG[locale]?.[itemName] || PRODUCT_CONFIG.jp[itemName];
    const items = [
        {
            title: t('item1_name'),
            texts: [t('item1_text1'), t('item1_text2'), t('item1_text3'), t('item1_text4'), t('item1_text5')],
            productId: getProductConfig('item1').productId,
            variantId: getProductConfig('item1').variantId,
        },
        {
            title: t('item2_name'),
            texts: [t('item2_text1'), t('item2_text2'), t('item2_text3'), t('item2_text4'), t('item2_text5')],
            productId: getProductConfig('item2').productId,
            variantId: getProductConfig('item2').variantId,
        },
    ];

    const getAsynItem = async (productId: string, variantId: string) => {
        return await new Promise(async (resolve, reject) => {
            try {
                const res = await getSolarRecommand({ productId, variantId }, locale);
                if (res?.code === '0') {
                    return resolve(res.data);
                } else {
                    console.error(res?.message);
                    return reject(res?.message);
                }
            } catch (e) {
                console.error(e);
                return reject(e);
            }
        });
    };

    const addToCart = (info: any) => {
        if ((Number(info?.inventory || 0) > 0 || info?.inventoryPolicy === 'continue') && callbackCart) {
            callbackCart({
                ...info,
            });
        }
        gtm.push({
            event: 'uaEvent',
            event_name: 'bundle_add_to_cart',
            list_name: t('title'),
            product_name: t('title'),
            price: info.discountPrice,
            tag: 'none',
            quantity: 1,
            button_name: t('right_btn') || i18n.link_cart,
            __btn_id1: 'ef-swiper-card-bundle',
            __btn_id2: t('right_btn') || i18n.link_cart,
            __btn_type: 'common',
        });
    };

    useEffect(() => {
        const promises = items?.map((item: any) => getAsynItem(item?.productId, item.variantId));
        Promise.all(promises).then((res) => {
            const old = JSON.parse(JSON.stringify(items));
            const list = res.map((i: any, idx) => {
                if (i.variantImg) {
                    old[idx].img = i.variantImg;
                }
                if (i?.price) {
                    old[idx].discountPrice = i.price;
                }
                if (i?.compareAtPrice ?? i?.price) {
                    old[idx].originalPrice = i?.compareAtPrice ?? i?.price;
                }
                if (i?.inventoryQuantity) {
                    old[idx].inventory = i?.inventoryQuantity || 0;
                }

                old[idx].inventory = i?.inventoryQuantity || 0;
                old[idx].href =
                    idx === 1
                        ? `https://${locale}.ecoflow.com/products/delta-3-ultra-portable-station?variant=${old[idx].variantId}`
                        : `https://${locale}.ecoflow.com/products/delta-3-ultra-plus-portable-station?variant=${old[idx].variantId}`;

                return old[idx];
            });
            setList(list || []);
            console.log('list', list, res);
        });
    }, []);

    return (
        <section
            className={`${css.section} D3U-Section-Card GtmModuleData-section`}
            id="D3U-Section-Card"
            data-section-id={'D3U-Section-Card'}
            data-section-id2={t('title')}
        >
            <div className="info">
                <p className="title">{t('title')}</p>
            </div>
            <ul className="items">
                {list.map((item, idx) => (
                    <li className={`item item-${idx + 1}`} key={idx}>
                        <div className="card">
                            <div className="image-col">
                                <div className="image-box" aria-hidden="true">
                                    <EcoImg pc={item.img} />
                                </div>
                            </div>
                            <div className="info-col">
                                <p className="badge">NEW</p>
                                <h3 className="title">{item.title}</h3>
                                <ul className="bullets">
                                    {item.texts.map((txt, i) => (
                                        <li key={i} className="bullet">
                                            <span className="dot" />
                                            <span className="text">{txt}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="price-row">
                                    {item.discountPrice && (
                                        <span className="price">
                                            {i18n.price_prefix}
                                            {FormatMoneyLocal(item.discountPrice, locale)}
                                            {i18n.price_suffix}
                                        </span>
                                    )}
                                    {item.originalPrice && item.originalPrice !== items.discountPrice && (
                                        <span className="compare">
                                            {i18n.price_prefix}
                                            {FormatMoneyLocal(item.originalPrice, locale)}
                                            {i18n.price_suffix}
                                        </span>
                                    )}
                                </div>
                                <div className="actions">
                                    <a className="btn btn-primary" href={item?.href} target="_blank" rel="noreferrer">
                                        {t('left_btn')}
                                    </a>
                                    <a className="btn btn-outline" onClick={() => addToCart(item)}>
                                        {t('right_btn')}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
            <div className="tip">{t('tip')}</div>
        </section>
    );
};

export default SectionCard;
